import {
  Box,
  Card,
  Typography,
  Grid,
  Divider,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  ResponsiveContainer,
  AreaChart,
  Area,
  LabelList,
} from "recharts";

import {
  StartLoading,
  StopLoading,
} from "../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import { useDispatch } from "react-redux";
import {
  ShowError,
  ShowSuccess,
} from "../../../Shared-ui/Store/MessageReducer/MsgActions";
import BaseInstance from "../../../Services/AxiosInstance/BaseInstance";
import {
  DASHBOARD_ACTIVITY,
  DASHBOARD_AGENT,
  DASHBOARD_CONVERSATION_METRICS,
  DASHBOARD_KNOWLEDGE_BASE,
  DASHBOARD_TEAMS,
} from "../../../Services/Endpoint";
import { useEffect, useState } from "react";
import ConversationActivityIcon from "../../../assets/images/grothIcon.svg";
import AIModelIcon from "../../../assets/images/AIModelIcon.svg";
import ActiveAgents from "../../../assets/images/ActiveAgentsIcon.svg";
import KnowledgeBase from "../../../assets/images/KnowledgeBaseIcon.svg";
import TeamStatus from "../../../assets/images/TeamStatusIcon.svg";
import ToolUsage from "../../../assets/images/ToolUsageIcon.svg";
import { set } from "date-fns";
import { capitalizeFirstLetter } from "../../../Shared-ui/utils/Common/Common";

interface MyComponentProps {
  refreshKey: number;
}

interface EmptyStateProps {
  message: string;
  subMessage?: string;
}
const DashboardGraphs: React.FC<MyComponentProps> = (props) => {
  const dispatch = useDispatch();
  const [lineChartData, setLineChartData] = useState<any[]>([]);
  const [pieChartData, setPieChartData] = useState<any[]>([]);
  const [kwBarChartData, setKwBarChartData] = useState<any[]>([]);
  const [teamStatus, setTeamStatus] = useState<any[]>([]);
  const [tokenRows, setTokenRows] = useState<any[]>([]);
  const [performanceRows, setPerformanceRows] = useState<any[]>([
    { name: "Active Agents", value: 0 },
    { name: "Active Teams", value: 0 },
    { name: "Avg Team Size", value: 0 },
    { name: "Docs per KB", value: 0 },
    { name: "24h Activity", value: 0 },
    { name: "7d Activity", value: 0 },
  ]);
  const [systemRows, setSystemRows] = useState<any[]>([
    { name: "Total Conversations", value: 0 },
    { name: "Avg Msg/Conv", value: 0 },
    { name: "Processing Status", value: `0 Complete` },
    { name: "Agents w/ Tools", value: 0 },
    { name: "Agents w/ Knowledge", value: 0 },
  ]);
  const [mostActiveAgent, setMostActiveAgent] = useState<any[]>([
  ])
  const [toolsUsage, setToolsUsage] = useState<any[]>([
  ])

  const [popularAgents, setPopularAgents] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      await getAgentData();
      await getTeamData();
      await getKnowledgeData();
      await getActivityData();
      await getToolUsageData();
    };
    fetchData();
  }, [props.refreshKey]);

  const getActivityData = async () => {
    try {
      dispatch(StartLoading());
      const data = await BaseInstance.get(DASHBOARD_ACTIVITY).then(
        (res) => res?.data
      );
      setLineChartData(data.conversation_trends);
      setPopularAgents(data.popular_agents);
      setPerformanceRows(preVal => [
        ...preVal.filter(
          row => row.name !== "24h Activity" && row.name !== "7d Activity"
        ),
        { name: "24h Activity", value: data.activity_24h },
        { name: "7d Activity", value: data.activity_7d },
      ]);
      setSystemRows(preVal => [
        ...preVal.filter(
          row => row.name !== "Total Conversations" && row.name !== "Avg Msg/Conv"
        ),
        { name: "Total Conversations", value: data.total_conversations },
        { name: "Avg Msg/Conv", value: data.avg_messages_per_conversation },
      ]);

      setMostActiveAgent(data.popular_agents.map((agent: any) => ({
        name: agent.name,
        value: agent.conversation_count
      })));
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading());
    }
  };

  const getTeamData = async () => {
    try {
      dispatch(StartLoading());
      const data = await BaseInstance.get(DASHBOARD_TEAMS).then(
        (res) => res?.data
      );
      const by_status = data?.by_status || {};
      const statusNames = Object.keys(by_status);
      const numStatus = statusNames.length;

      const startColor = "#04db93"; // greenish
      const endColor = "#01aeee"; // bluish

      const generatedColors = statusNames.map((_, idx) =>
        interpolateColor(startColor, endColor, idx / Math.max(numStatus - 1, 1))
      );

      const pieChartData = statusNames.map((name, idx) => ({
        name,
        value: by_status[name],
        color: generatedColors[idx],
      }));
      setTeamStatus(pieChartData);
      setPerformanceRows(preVal => [
        ...preVal.filter(
          row => row.name !== "Active Teams" && row.name !== "Avg Team Size"
        ),
        { name: "Active Teams", value: data.by_status.active },
        { name: "Avg Team Size", value: data.average_team_size },
      ]);
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading());
    }
  };



  const getToolUsageData = async () => {
    try {
      dispatch(StartLoading());
      const data = await BaseInstance.get(DASHBOARD_CONVERSATION_METRICS).then(
        (res) => res?.data
      );
      setToolsUsage(
        Object.entries(data.tool_usage_stats).map(([name, usage_count]) => ({
          name,
          value: usage_count
        }))
      );
      setTokenRows((prev) => [
        createData("Input", data.token_distribution.input),
        createData("Output", data.token_distribution.output),
        createData("Avg Tokens/Conv", data.avg_tokens_per_conversation),
      ]);

    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading());
    }
  };

  const interpolateColor = (color1: string, color2: string, factor: number) => {
    const c1 = hexToRgb(color1);
    const c2 = hexToRgb(color2);

    const result = {
      r: Math.round(c1.r + (c2.r - c1.r) * factor),
      g: Math.round(c1.g + (c2.g - c1.g) * factor),
      b: Math.round(c1.b + (c2.b - c1.b) * factor),
    };

    return rgbToHex(result.r, result.g, result.b);
  };

  const hexToRgb = (hex: string) => {
    const cleanHex = hex.replace("#", "");
    const bigint = parseInt(cleanHex, 16);
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255,
    };
  };

  const rgbToHex = (r: number, g: number, b: number) =>
    "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");

  const getAgentData = async () => {
    try {
      dispatch(StartLoading());

      const data = await BaseInstance.get(DASHBOARD_AGENT).then(
        (res) => res?.data
      );

      const by_model = data?.by_model || {};
      const modelNames = Object.keys(by_model);
      const numModels = modelNames.length;

      const startColor = "#04db93"; // greenish
      const endColor = "#01aeee"; // bluish

      const generatedColors = modelNames.map((_, idx) =>
        interpolateColor(startColor, endColor, idx / Math.max(numModels - 1, 1))
      );

      const pieChartData = modelNames.map((name, idx) => ({
        name: capitalizeFirstLetter(name.split(":")[1]),
        value: by_model[name],
        color: generatedColors[idx],
      }));
      setPerformanceRows(preVal => [
        ...preVal.filter(
          row => row.name !== "Active Agents"
        ),
        { name: "Active Agents", value: ` ${data.by_status.active} / ${data.total_own}` },
      ]);

      setSystemRows(preVal => [
        ...preVal.filter(
          row => row.name !== "Agents w/ Tools" && row.name !== "Agents w/ Knowledge"
        ),
        { name: "Agents w/ Tools", value: data.with_tools },
        { name: "Agents w/ Knowledge", value: data.with_knowledge },
      ]);
      setPieChartData(pieChartData);
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading());
    }
  };

  const getKnowledgeData = async () => {
    try {
      dispatch(StartLoading());
      const data = await BaseInstance.get(DASHBOARD_KNOWLEDGE_BASE).then(
        (res) => res?.data
      );
      setKwBarChartData(data.documents_per_kb);
      setPerformanceRows(preVal => [
        ...preVal.filter(
          row => row.name !== "Docs per KB"
        ),
        { name: "Docs per KB", value: data.total_documents },
      ]);

      setSystemRows(preVal => [
        ...preVal.filter(
          row => row.name !== "Processing Status"
        ),
        { name: "Processing Status", value: `${data.file_processing_status.completed} Complete` },
      ]);
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading());
    }
  };


  function createData(name: string, value: number | string) {
    return { name, value };
  }

  function todayDate() {
    return `${new Date().getDate().toString().padStart(2, "0")}/${(new Date().getMonth() + 1)
      .toString()
      .padStart(2, "0")}`;
  }

  const EmptyState = ({ message, subMessage }: EmptyStateProps) => (
  <Box textAlign="center" p={4}>
    <Typography variant="h6" color="text.secondary" gutterBottom>
      {message}
    </Typography>
    {subMessage && (
      <Typography variant="body2" color="text.secondary">
        {subMessage}
      </Typography>
    )}
  </Box>
);

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 3,
        }}
      >
        <Card
          sx={{
            p: 3,
            borderRadius: 2,
            boxShadow: "0px 0px 15px rgba(0, 0, 0, 0.1)",
            backdropFilter: "blur(5px)",
            backgroundColor: "rgba(256, 256, 256, 0.7)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Box
              sx={{
                bgcolor: "#02BEDB",
                width: 40,
                height: 40,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 2,
              }}
            >
              <img src={ConversationActivityIcon} alt="Conversation Activity" />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={500}>
                Conversation Activity Trend
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Daily conversation volume over the last 7 days
              </Typography>
            </Box>
          </Box>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={lineChartData.length > 0 ? lineChartData : [{ date: todayDate(), conversations: 0 }]}>
              <defs>
                <linearGradient id="colorConv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#02BEDB" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="#02BEDB" stopOpacity={0.2} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(date) => {
                  // If date is already in "DD/MM" format, convert to "DD Month"
                  if (date.includes("/")) {
                    const [day, month] = date.split("/");
                    const monthNames = [
                      "January",
                      "February",
                      "March",
                      "April",
                      "May",
                      "June",
                      "July",
                      "August",
                      "September",
                      "October",
                      "November",
                      "December",
                    ];
                    return `${day} ${monthNames[parseInt(month, 10) - 1]}`;
                  }
                  // If date is ISO or other format, fallback to Date object
                  const d = new Date(date);
                  const monthNames = [
                    "January",
                    "February",
                    "March",
                    "April",
                    "May",
                    "June",
                    "July",
                    "August",
                    "September",
                    "October",
                    "November",
                    "December",
                  ];
                  return `${d.getDate()} ${monthNames[d.getMonth()]}`;
                }}
              />
              <YAxis />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="conversations"
                stroke="#02BEDB"
                fill="url(#colorConv)"
                strokeWidth={3}
                dot={{ r: 5, stroke: "#02BEDB", strokeWidth: 2, fill: "#fff" }}
                activeDot={{ r: 7, fill: "#02BEDB" }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card
          sx={{
            p: 3,
            borderRadius: 2,
            boxShadow: "0px 0px 15px rgba(0, 0, 0, 0.1)",
            backdropFilter: "blur(5px)",
            backgroundColor: "rgba(256, 256, 256, 0.7)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Box
              sx={{
                bgcolor: "#1DDF9E",
                width: 40,
                height: 40,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 2,
              }}
            >
              <img src={AIModelIcon} alt="AI Model Usage" />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={500}>
                AI Model Usage
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Usage breakdown by AI model
              </Typography>
            </Box>
          </Box>

          {pieChartData && pieChartData.length > 0 ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      startAngle={90}
                      endAngle={450}
                      label={({ value }) => value}
                      labelLine={false}
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </Grid>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {pieChartData.map((entry, idx) => (
                    <Box
                      key={idx}
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Box
                        sx={{
                          width: 14,
                          height: 14,
                          borderRadius: "50%",
                          bgcolor: entry.color,
                          mr: 1,
                        }}
                      />
                      <Typography variant="body2" sx={{ minWidth: 80 }}>
                        {entry.name}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {entry.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          ) : (
            <Box
              sx={{
                height: 260, // Match the height of the charts
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <EmptyState
                message="No AI model usage data available."
                // subMessage="Start a conversation to see usage statistics here."
              />
            </Box>
          )}
        </Card>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 3,
          mt: 3,
        }}
      >
        <Card
          sx={{
            p: 3,
            borderRadius: 2,
            boxShadow: "0px 0px 15px rgba(0, 0, 0, 0.1)",
            backdropFilter: "blur(5px)",
            backgroundColor: "rgba(256, 256, 256, 0.7)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Box
              sx={{
                bgcolor: "#05DABB",
                width: 40,
                height: 40,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 2,
              }}
            >
              <img src={KnowledgeBase} alt="Knowledge Base Distribution" />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={500}>
                Knowledge Base Distribution
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Document count per knowledge base
              </Typography>
            </Box>
          </Box>
          {kwBarChartData && kwBarChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={kwBarChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="kb_name"
                  interval={0}
                  height={80}
                  angle={-45}
                  textAnchor="end"
                  tick={{ fontSize: 15 }}
                  tickFormatter={(name) =>
                    name.length > 10 ? name.slice(0, 10) + "..." : name
                  }
                />
                <YAxis
                  tick={{ fontSize: 15 }}
                  domain={[0, "dataMax + 200"]}
                  allowDecimals={false}
                />
                <Tooltip
                  wrapperStyle={{ boxShadow: "none", borderRadius: 8 }}
                  contentStyle={{ borderRadius: 8 }}
                />
                <Bar
                  dataKey="document_count"
                  fill="#05DABB"
                  radius={[4, 4, 4, 4]}
                  barSize={60}
                >
                  <LabelList
                    dataKey="document_count"
                    position="top"
                    fontSize={10}
                    fontWeight={500}
                    fill="#222"
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box
              sx={{
                height: 320, // Match the height of the chart container
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <EmptyState
                message="No knowledge bases found."
                // subMessage="Add some knowledge bases to see their distribution here."
              />
            </Box>
          )}
        </Card>

        <Card
          sx={{
            p: 3,
            borderRadius: 2,
            boxShadow: "0px 0px 15px rgba(0, 0, 0, 0.1)",
            backdropFilter: "blur(5px)",
            backgroundColor: "rgba(256, 256, 256, 0.7)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Box
              sx={{
                bgcolor: "#01AEEE",
                width: 40,
                height: 40,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 2,
              }}
            >
              <img src={ActiveAgents} alt="Conversation Activity" />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={500}>
                Most Active Agents
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Top performing agents by conversation count
              </Typography>
            </Box>
          </Box>
          {mostActiveAgent && mostActiveAgent.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={mostActiveAgent}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  interval={0}
                  height={80}
                  angle={-45}
                  textAnchor="end"
                  tick={{ fontSize: 15 }}
                  tickFormatter={(name) =>
                    name.length > 10 ? name.slice(0, 10) + "..." : name
                  }
                />
                <YAxis
                  tick={{ fontSize: 15 }}
                  domain={[0, "dataMax + 200"]}
                  allowDecimals={false}
                />
                <Tooltip
                  wrapperStyle={{ boxShadow: "none", borderRadius: 8 }}
                  contentStyle={{ borderRadius: 8 }}
                />
                <Bar
                  dataKey="value"
                  fill="#01AEEE"
                  radius={[4, 4, 4, 4]}
                  barSize={60}
                >
                  <LabelList
                    dataKey="value"
                    position="top"
                    fontSize={10}
                    fontWeight={500}
                    fill="#222"
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box
              sx={{
                height: 320, // Match the height of the chart container
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <EmptyState
                message="No active agents found."
                // subMessage="Agents will appear here once they start conversations."
              />
            </Box>
          )}
        </Card>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 3,
          mt: 3,
        }}
      >
        <Card
          sx={{
            p: 3,
            borderRadius: 2,
            boxShadow: "0px 0px 15px rgba(0, 0, 0, 0.1)",
            backdropFilter: "blur(5px)",
            backgroundColor: "rgba(256, 256, 256, 0.7)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Box
              sx={{
                bgcolor: "#02BEDB",
                width: 40,
                height: 40,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 2,
              }}
            >
              <img src={TeamStatus} alt="AI Model Usage" />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={500}>
                Team Status Overview
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Current team status distribution
              </Typography>
            </Box>
          </Box>

          {teamStatus && teamStatus.length > 0 ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={teamStatus}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      startAngle={90}
                      endAngle={450}
                      label={({ value }) => value}
                      labelLine={false}
                    >
                      {teamStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </Grid>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {teamStatus.map((entry, idx) => (
                    <Box
                      key={idx}
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Box
                        sx={{
                          width: 14,
                          height: 14,
                          borderRadius: "50%",
                          bgcolor: entry.color,
                          mr: 1,
                        }}
                      />
                      <Typography variant="body2" sx={{ minWidth: 80 }}>
                        {capitalizeFirstLetter(entry.name)}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {entry.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          ) : (
            <Box
              sx={{
                height: 260, // Match the height of the chart container
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <EmptyState
                message="No team status data found."
                // subMessage="Team status will be displayed here after you create teams."
              />
            </Box>
          )}
        </Card>
        <Card
          sx={{
            p: 3,
            borderRadius: 2,
            boxShadow: "0px 0px 15px rgba(0, 0, 0, 0.1)",
            backdropFilter: "blur(5px)",
            backgroundColor: "rgba(256, 256, 256, 0.7)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Box
              sx={{
                bgcolor: "#04DB93",
                width: 40,
                height: 40,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 2,
              }}
            >
              <img src={ToolUsage} alt="Tool Usage" />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={500}>
                Tool Usage Statistics
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Frequency of tool usage across conversations
              </Typography>
            </Box>
          </Box>

          {toolsUsage && toolsUsage.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={toolsUsage}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  interval={0}
                  height={80}
                  angle={-45}
                  textAnchor="end"
                  tick={{ fontSize: 15 }}
                  tickFormatter={(name) =>
                    name.length > 10 ? name.slice(0, 10) + "..." : name
                  }
                />
                <YAxis
                  tick={{
                    fontSize: 15,
                  }}
                  domain={[0, "dataMax + 200"]}
                  allowDecimals={false}
                />
                <Tooltip
                  wrapperStyle={{ boxShadow: "none", borderRadius: 8 }}
                  contentStyle={{ borderRadius: 8 }}
                />
                <Bar
                  dataKey="value"
                  fill="#04DB93"
                  radius={[4, 4, 4, 4]}
                  barSize={60}
                >
                  <LabelList
                    dataKey="value"
                    position="top"
                    fontSize={10}
                    fontWeight={500}
                    fill="#222"
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box
              sx={{
                height: 320, // Match the height of the chart container
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <EmptyState
                message="No tool usage data available."
                // subMessage="This chart will display data as tools are used in conversations."
              />
            </Box>
          )}
        </Card>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
          gap: 3,
          mt: 3,
        }}
      >
        <Card
          sx={{
            p: 3,
            borderRadius: 2,
            boxShadow: "0px 0px 15px rgba(0, 0, 0, 0.1)",
            backdropFilter: "blur(5px)",
            backgroundColor: "rgba(256, 256, 256, 0.7)",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="h6" fontWeight={500}>
              Token Distribution
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Input vs Output token usage
            </Typography>
          </Box>
          <TableContainer>
            <Table sx={{ minWidth: 200 }} aria-label="token table">
              <TableBody>
                {tokenRows.map((row) => (
                  <TableRow
                    key={row.name}
                    sx={{
                      "&:last-child td, &:last-child th": {
                        border: 0,
                        fontWeight: 500,
                      },
                    }}
                  >
                    <TableCell component="th" scope="row">
                      {row.name}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 500 }}>
                      {row.value}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
        <Card
          sx={{
            p: 3,
            borderRadius: 2,
            boxShadow: "0px 0px 15px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="h6" fontWeight={500}>
              Performance Summary
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Key performance indicators
            </Typography>
          </Box>
          <TableContainer>
            <Table sx={{ minWidth: 200 }} aria-label="performance table">
              <TableBody>
                {performanceRows.map((row) => (
                  <TableRow
                    key={row.name}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    <TableCell component="th" scope="row">
                      {row.name}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 500 }}>
                      {row.value}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
        <Card
          sx={{
            p: 3,
            borderRadius: 2,
            boxShadow: "0px 0px 15px rgba(0, 0, 0, 0.1)",
            backdropFilter: "blur(5px)",
            backgroundColor: "rgba(256, 256, 256, 0.7)",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="h6" fontWeight={500}>
              System Overview
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Current system status
            </Typography>
          </Box>
          <TableContainer>
            <Table sx={{ minWidth: 200 }} aria-label="system table">
              <TableBody>
                {systemRows.map((row) => (
                  <TableRow
                    key={row.name}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    <TableCell component="th" scope="row">
                      {row.name}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 500 }}>
                      {row.value}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>
    </>
  );
};

export default DashboardGraphs;
